import * as React from 'react'
import {cn} from '@/lib/utils'
import {topeDeTexto} from '@/lib/campos-texto'
import {AvisoDeTope, useAvisoDeTope} from '@/components/ui/aviso-de-tope'

/** `maxLength` por defecto 256, como `ui/input.tsx`. Crece con el contenido (D7) midiendo `scrollHeight`
 * porque Safari y Firefox no soportan `field-sizing: content`. */
function Textarea({
  className,
  minRows = 2,
  maxRows = 10,
  maxLength,
  onInput,
  onPaste,
  ...props
}: React.ComponentProps<'textarea'> & {minRows?: number; maxRows?: number}) {
  const ref = React.useRef<HTMLTextAreaElement>(null)
  const tope = topeDeTexto(undefined, maxLength)
  const aviso = useAvisoDeTope(tope, props.value)

  const resize = React.useCallback(
    (el: HTMLTextAreaElement) => {
      // text-sm con interlineado 1.5 (0.875rem × 1.5); con 20 el campo gana scroll un renglón antes.
      const lineHeight = 21
      const minHeight = minRows * lineHeight + 16 // + vertical padding (py-2 = 8px * 2)
      const maxHeight = maxRows * lineHeight + 16
      el.style.height = 'auto'
      el.style.height = `${Math.min(Math.max(el.scrollHeight, minHeight), maxHeight)}px`
    },
    [minRows, maxRows]
  )

  React.useEffect(() => {
    if (ref.current) resize(ref.current)
  }, [resize, props.value])

  const campo = (
    <textarea
      ref={ref}
      maxLength={tope}
      data-slot="textarea"
      onPaste={(e) => {
        aviso.onPaste(e)
        onPaste?.(e)
      }}
      onInput={(e) => {
        aviso.olvidarRecorte()
        resize(e.currentTarget)
        onInput?.(e)
      }}
      className={cn(
        'w-full resize-none overflow-y-auto rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground transition-colors outline-none placeholder:text-muted-foreground hover:border-[color-mix(in_oklch,var(--input),var(--foreground)_18%)] focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:opacity-70 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20',
        className
      )}
      {...props}
    />
  )
  // Solo el campo largo (≥ 500) se envuelve: el resto conserva el DOM de las líneas base visuales.
  if (aviso.recortadoA === null && aviso.restantes === null) return campo
  return (
    <>
      {campo}
      <AvisoDeTope recortadoA={aviso.recortadoA} restantes={aviso.restantes} />
    </>
  )
}

export {Textarea}
